import { useState, useEffect, useMemo } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  Target, Plus, Archive, Edit, Loader2, Search, Play, Pause, Clock, 
  DollarSign, Copy, AlertTriangle, MapPin, MoreHorizontal, Building2, Map, X
} from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { NONE_SELECTED } from "@/lib/audienceSummary";

// A local calendar date, YYYY-MM-DD (round 11).
//
// `new Date().toISOString()` converts to UTC first. Australia is UTC+10 or +11, so for
// the first ten hours of every local day it returns YESTERDAY - which is how Robin
// could pick a start date of 10 September at 9:26am on the 11th. `en-CA` is the
// shortest way to get an ISO-shaped date in the LOCAL zone without a date library.
function toLocalDateString(date) {
  return date.toLocaleDateString("en-CA");
}

const campaignStatuses = [
  { value: "DRAFT", label: "Draft", color: "bg-gray-500" },
  { value: "ACTIVE", label: "Active", color: "bg-green-500" },
  { value: "PAUSED", label: "Paused", color: "bg-yellow-500" },
  { value: "COMPLETED", label: "Completed", color: "bg-blue-500" },
];

// Australian states for targeting
const AUSTRALIAN_STATES = [
  { code: "NSW", name: "New South Wales" },
  { code: "VIC", name: "Victoria" },
  { code: "QLD", name: "Queensland" },
  { code: "WA", name: "Western Australia" },
  { code: "SA", name: "South Australia" },
  { code: "TAS", name: "Tasmania" },
  { code: "ACT", name: "Australian Capital Territory" },
  { code: "NT", name: "Northern Territory" }
];

export default function AdvertiserCampaigns() {
  const { advertiser, advertiserId } = useOutletContext();
  const { token } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [locations, setLocations] = useState([]);
  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [saving, setSaving] = useState(false);
  
  // Archive modal state
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [campaignToArchive, setCampaignToArchive] = useState(null);
  const [archiving, setArchiving] = useState(false);

  // Reprice confirmation. A paid campaign's priced fields are locked; an admin can
  // override, but only deliberately and it is logged for review.
  const [repriceConfirm, setRepriceConfirm] = useState(null);
  
  // Location targeting modal
  const [locationTargetingOpen, setLocationTargetingOpen] = useState(false);
  const [locationSearch, setLocationSearch] = useState("");
  const [locationSearchResults, setLocationSearchResults] = useState([]);
  const [searchingLocations, setSearchingLocations] = useState(false);
  
  // Form state - EXACT alignment with advertiser Campaigns.jsx
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: "DRAFT",
    surfaces: ["PASSENGER_APP", "PANEL"],
    start_at: "",
    end_at: "",
    budget_limit: "",
    pricing: {
      cpm_enabled: true,
      cpm_rate: 5.0,
      cpc_enabled: false,
      cpc_rate: 0.35,
      cpdc_direct_enabled: false,
      cpdc_direct_rate: 2.5,
      cpdc_proximity_enabled: false,
      cpdc_proximity_rate: 1.0,
      cpdc_prompted_enabled: false,
      cpdc_prompted_rate: 3.0
    },
    creative_ids: [],
    deal_available: false,
    deal_description: "",
    offer_text: "",
    cta_text: "Claim Deal",
    targeting_config: {
      location_ids: [],
      states: [],
      postcodes: [],
      suburbs: [],
      radius_override: null,
      days: ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"],
      time_slots: ["morning", "afternoon", "evening", "night"],
      gender: "all",
      // Lists, matching CampaignTargeting. The controls below are single-select
      // and hold one item; the names are the model's so there is nothing to
      // translate and nothing to get wrong.
      age_ranges: ["all"],
      venue_types: ["any"],
      interests: ["any"]
    }
  });

  useEffect(() => {
    fetchData();
  }, [advertiserId]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [campaignsRes, locationsRes, creativesRes] = await Promise.all([
        axios.get(`${API}/accounts/${advertiserId}/campaigns`, { headers }),
        axios.get(`${API}/accounts/${advertiserId}/locations`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/accounts/${advertiserId}/creatives`, { headers }).catch(() => ({ data: [] }))
      ]);
      setCampaigns(campaignsRes.data);
      setLocations(locationsRes.data);
      setCreatives(creativesRes.data);
    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  // Search for suburbs/postcodes
  const searchLocations = async (query) => {
    if (!query || query.length < 2) {
      setLocationSearchResults([]);
      return;
    }
    
    setSearchingLocations(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/targeting/search-locations`, {
        headers,
        params: { query }
      });
      setLocationSearchResults(response.data || []);
    } catch (error) {
      console.error("Location search error:", error);
      setLocationSearchResults([]);
    } finally {
      setSearchingLocations(false);
    }
  };

  // Debounced location search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (locationSearch) {
        searchLocations(locationSearch);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [locationSearch]);

  const filteredCampaigns = useMemo(() => {
    return campaigns.filter(c => {
      if (searchQuery && !c.name?.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (filterStatus !== "all" && c.status !== filterStatus) return false;
      return true;
    });
  }, [campaigns, searchQuery, filterStatus]);

  // Get today's date in YYYY-MM-DD format for min date validation
  const getTodayDate = () => {
    return toLocalDateString(new Date());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Please enter a campaign name");
      return;
    }
    
    // Validate required dates
    if (!formData.start_at) {
      toast.error("Start date is required");
      return;
    }
    if (!formData.end_at) {
      toast.error("End date is required");
      return;
    }
    
    // Validate start date is not before today (only for new campaigns)
    if (!editingCampaign) {
      const today = getTodayDate();
      if (formData.start_at < today) {
        toast.error("Start date cannot be before today");
        return;
      }
    }
    
    // Validate end date is after start date
    if (formData.end_at < formData.start_at) {
      toast.error("End date must be after start date");
      return;
    }
    
    const payload = {
      name: formData.name,
      description: formData.description,
      status: formData.status,
      surfaces: formData.surfaces,
      start_at: formData.start_at || null,
      end_at: formData.end_at || null,
      budget_limit: formData.budget_limit ? parseFloat(formData.budget_limit) : null,
      pricing: formData.pricing,
      creative_ids: formData.creative_ids,
      deal_available: formData.deal_available,
      deal_description: formData.deal_description || null,
      offer_text: formData.offer_text || null,
      cta_text: formData.cta_text || null,
      targeting_config: {
        location_ids: formData.targeting_config.location_ids,
        states: formData.targeting_config.states,
        postcodes: formData.targeting_config.postcodes,
        suburbs: formData.targeting_config.suburbs,
        radius_override: formData.targeting_config.radius_override,
        days: formData.targeting_config.days,
        time_slots: formData.targeting_config.time_slots,
        gender: formData.targeting_config.gender,
        // Round 9. This page sent age_range, venue_type and a STRING interests -
        // the shape CampaignTargeting had before they became multi-select. The
        // string 422'd every save, so the admin campaign editor did not work at
        // all; the other two were dropped by Pydantic and their real fields, never
        // sent, reset to defaults, which would have wiped a Gold merchant's
        // paid-for targeting the moment a save did land.
        age_ranges: formData.targeting_config.age_ranges,
        venue_types: formData.targeting_config.venue_types,
        interests: formData.targeting_config.interests,
      },
      account_id: advertiserId
    };

    setSaving(true);
    try {
      if (editingCampaign) {
        await axios.put(`${API}/campaigns/${editingCampaign.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Campaign updated");
      } else {
        // Draft-then-pay: POST /campaigns saves a DRAFT and charges nothing, so an
        // admin-created campaign now needs an explicit launch to go live. Without this
        // second call it would sit as a draft forever and nobody would know why —
        // creation used to take it live in one step.
        //
        // Launch waives the charge for an admin and marks the campaign paid, because it
        // IS settled: the advertiser is billed separately. That honesty is what let the
        // activation gate drop its role-based bypass.
        const created = await axios.post(`${API}/campaigns`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const newId = created.data?.campaign?.id;
        if (newId) {
          try {
            await axios.post(`${API}/campaigns/${newId}/launch`, {}, {
              headers: { Authorization: `Bearer ${token}` }
            });
            toast.success("Campaign created and live");
          } catch (launchError) {
            // The campaign is saved either way. assert_launch_ready names what is
            // missing, so pass it through rather than reporting a create failure for
            // something that created fine.
            toast.warning("Saved as a draft", {
              description: launchError.response?.data?.detail
                || "Could not launch it yet.",
            });
          }
        } else {
          toast.success("Campaign created");
        }
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      // A paid campaign's priced fields (tier, duration, pricing) are locked. The
      // backend decides which of them this edit actually changes, so surface its
      // message and offer the audited override rather than duplicating that
      // comparison — and drifting from it — here.
      if (error.response?.status === 409 && editingCampaign) {
        setRepriceConfirm({
          campaignId: editingCampaign.id,
          campaignName: editingCampaign.name,
          detail: error.response?.data?.detail || "",
          payload
        });
        return;
      }
      toast.error(error.response?.data?.detail || "Failed to save campaign");
    } finally {
      setSaving(false);
    }
  };

  // Retry the edit with the admin override. Recorded server-side as an AdminAlert
  // ("paid_campaign_repriced") carrying the actor, every changed field and the
  // tier_cost delta.
  const confirmReprice = async () => {
    if (!repriceConfirm) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/campaigns/${repriceConfirm.campaignId}?override_priced_fields=true`,
        repriceConfirm.payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Campaign repriced, and logged for review");
      setRepriceConfirm(null);
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reprice campaign");
    } finally {
      setSaving(false);
    }
  };

  const openArchiveModal = (campaign) => {
    setCampaignToArchive(campaign);
    setArchiveModalOpen(true);
  };

  const confirmArchive = async () => {
    if (!campaignToArchive) return;
    setArchiving(true);
    try {
      await axios.post(`${API}/campaigns/${campaignToArchive.id}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Campaign archived");
      setArchiveModalOpen(false);
      setCampaignToArchive(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to archive");
    } finally {
      setArchiving(false);
    }
  };

  const handleCopy = async (id) => {
    try {
      await axios.post(`${API}/campaigns/${id}/copy`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Campaign copied to drafts");
      fetchData();
    } catch (error) {
      toast.error("Failed to copy campaign");
    }
  };

  const handleToggleStatus = async (campaign) => {
    const newStatus = campaign.status === "ACTIVE" ? "PAUSED" : "ACTIVE";
    try {
      await axios.put(`${API}/campaigns/${campaign.id}`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Campaign ${newStatus === "ACTIVE" ? "activated" : "paused"}`);
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const openEditDialog = (campaign) => {
    setEditingCampaign(campaign);
    const tc = campaign.targeting_config || {};
    const pricing = campaign.pricing || {};
    
    setFormData({
      name: campaign.name || "",
      description: campaign.description || "",
      status: campaign.status || "DRAFT",
      surfaces: campaign.surfaces || ["PASSENGER_APP", "PANEL"],
      start_at: campaign.start_at ? campaign.start_at.split('T')[0] : "",
      end_at: campaign.end_at ? campaign.end_at.split('T')[0] : "",
      budget_limit: campaign.budget_limit || "",
      pricing: {
        cpm_enabled: pricing.cpm_enabled ?? true,
        cpm_rate: pricing.cpm_rate ?? 5.0,
        cpc_enabled: pricing.cpc_enabled ?? false,
        cpc_rate: pricing.cpc_rate ?? 0.35,
        cpdc_direct_enabled: pricing.cpdc_direct_enabled ?? false,
        cpdc_direct_rate: pricing.cpdc_direct_rate ?? 2.5,
        cpdc_proximity_enabled: pricing.cpdc_proximity_enabled ?? false,
        cpdc_proximity_rate: pricing.cpdc_proximity_rate ?? 1.0,
        cpdc_prompted_enabled: pricing.cpdc_prompted_enabled ?? false,
        cpdc_prompted_rate: pricing.cpdc_prompted_rate ?? 3.0
      },
      creative_ids: campaign.creative_ids || [],
      deal_available: campaign.deal_available || false,
      deal_description: campaign.deal_description || "",
      offer_text: campaign.offer_text || "",
      cta_text: campaign.cta_text || "Claim Deal",
      targeting_config: {
        location_ids: tc.location_ids || [],
        states: tc.states || [],
        postcodes: tc.postcodes || [],
        suburbs: tc.suburbs || [],
        radius_override: tc.radius_override || null,
        days: tc.days || ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"],
        time_slots: tc.time_slots || ["morning", "afternoon", "evening", "night"],
        gender: tc.gender || "all",
        // Read the list, show the first. Reading tc.age_range gave undefined for
        // every campaign, so the Selects always opened on their default whatever the
        // merchant had chosen.
        age_ranges: tc.age_ranges?.length ? tc.age_ranges : ["all"],
        venue_types: tc.venue_types?.length ? tc.venue_types : ["any"],
        interests: tc.interests?.length ? tc.interests : ["any"]
      }
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCampaign(null);
    setFormData({
      name: "",
      description: "",
      status: "DRAFT",
      surfaces: ["PASSENGER_APP", "PANEL"],
      start_at: "",
      end_at: "",
      budget_limit: "",
      pricing: {
        cpm_enabled: true, cpm_rate: 5.0,
        cpc_enabled: false, cpc_rate: 0.35,
        cpdc_direct_enabled: false, cpdc_direct_rate: 2.5,
        cpdc_proximity_enabled: false, cpdc_proximity_rate: 1.0,
        cpdc_prompted_enabled: false, cpdc_prompted_rate: 3.0
      },
      creative_ids: [],
      deal_available: false,
      deal_description: "",
      offer_text: "",
      cta_text: "Claim Deal",
      targeting_config: {
        location_ids: [], states: [], postcodes: [], suburbs: [],
        radius_override: null,
        days: ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"],
        time_slots: ["morning", "afternoon", "evening", "night"],
        gender: "all", age_ranges: ["all"], venue_types: ["any"], interests: ["any"]
      }
    });
    setLocationSearch("");
    setLocationSearchResults([]);
  };

  const getStatusBadge = (status) => {
    const s = campaignStatuses.find(x => x.value === status);
    return s ? (
      <Badge variant="outline">
        <span className={`w-2 h-2 rounded-full ${s.color} mr-1.5`}></span>
        {s.label}
      </Badge>
    ) : null;
  };

  // Get location targeting summary - EXACT match with advertiser view
  const getLocationTargetingSummary = () => {
    const parts = [];
    const t = formData.targeting_config;
    
    if (t.location_ids?.length > 0) {
      parts.push(`${t.location_ids.length} venue${t.location_ids.length > 1 ? 's' : ''}`);
    }
    if (t.states?.length > 0) {
      parts.push(`${t.states.length} state${t.states.length > 1 ? 's' : ''}`);
    }
    if (t.postcodes?.length > 0) {
      parts.push(`${t.postcodes.length} postcode${t.postcodes.length > 1 ? 's' : ''}`);
    }
    if (t.suburbs?.length > 0) {
      parts.push(`${t.suburbs.length} suburb${t.suburbs.length > 1 ? 's' : ''}`);
    }
    
    return parts.length > 0 ? parts.join(', ') : 'All Areas';
  };

  // Get targeting summary for campaign list
  const getCampaignTargetingSummary = (tc) => {
    if (!tc) return "All Areas";
    const parts = [];
    if (tc.location_ids?.length) parts.push(`${tc.location_ids.length} venue${tc.location_ids.length > 1 ? 's' : ''}`);
    if (tc.states?.length) parts.push(`${tc.states.length} state${tc.states.length > 1 ? 's' : ''}`);
    if (tc.postcodes?.length) parts.push(`${tc.postcodes.length} postcode${tc.postcodes.length > 1 ? 's' : ''}`);
    if (tc.suburbs?.length) parts.push(`${tc.suburbs.length} suburb${tc.suburbs.length > 1 ? 's' : ''}`);
    return parts.length > 0 ? parts.join(', ') : 'All Areas';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {campaignStatuses.map(status => {
          const count = campaigns.filter(c => c.status === status.value).length;
          return (
            <Card key={status.value} className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{status.label}</span>
                  <Badge className={`${status.color} text-white`}>{count}</Badge>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Search + Filters + Add */}
      <div className="flex gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Search campaigns..." 
            value={searchQuery} 
            onChange={(e) => setSearchQuery(e.target.value)} 
            className="pl-10" 
          />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {campaignStatuses.map(s => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }}>
          <Plus className="mr-2 h-4 w-4" />
          New Campaign
        </Button>
      </div>

      {/* Campaigns List */}
      {filteredCampaigns.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <Target className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No campaigns found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredCampaigns.map(campaign => {
            const tc = campaign.targeting_config || {};
            const pricing = campaign.pricing || {};
            
            return (
              <Card key={campaign.id} className="bg-card border-border overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: Campaign Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold truncate">{campaign.name}</h3>
                        {getStatusBadge(campaign.status)}
                      </div>
                      
                      {campaign.description && (
                        <p className="text-sm text-muted-foreground mb-3 line-clamp-1">{campaign.description}</p>
                      )}

                      {/* Date Range */}
                      {(campaign.start_at || campaign.end_at) && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                          <Clock className="h-4 w-4" />
                          <span>
                            {campaign.start_at ? new Date(campaign.start_at).toLocaleDateString('en-AU') : 'No start'} 
                            {' → '} 
                            {campaign.end_at ? new Date(campaign.end_at).toLocaleDateString('en-AU') : 'Ongoing'}
                          </span>
                        </div>
                      )}

                      {/* Quick Stats */}
                      <div className="flex flex-wrap gap-3 text-xs">
                        <span className="flex items-center gap-1 text-muted-foreground">
                          <MapPin className="h-3 w-3" />
                          {getCampaignTargetingSummary(tc)}
                        </span>
                        {campaign.budget_limit && (
                          <span className="flex items-center gap-1 text-muted-foreground">
                            <DollarSign className="h-3 w-3" />
                            ${campaign.budget_limit.toLocaleString()} budget
                          </span>
                        )}
                        <span className="text-muted-foreground">
                          {pricing.cpm_enabled && `Views: $${pricing.cpm_rate || 5}`}
                          {pricing.cpc_enabled && ` • Clicks: $${pricing.cpc_rate || 0.35}`}
                        </span>
                      </div>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-2">
                      {campaign.status !== "COMPLETED" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleStatus(campaign)}
                        >
                          {campaign.status === "ACTIVE" ? (
                            <><Pause className="h-4 w-4 mr-1" /> Pause</>
                          ) : (
                            <><Play className="h-4 w-4 mr-1" /> Activate</>
                          )}
                        </Button>
                      )}
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(campaign)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleCopy(campaign.id)}>
                            <Copy className="h-4 w-4 mr-2" />
                            Copy
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => openArchiveModal(campaign)}
                            className="text-amber-600 focus:text-amber-600"
                          >
                            <Archive className="h-4 w-4 mr-2" />
                            Archive
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit/Create Dialog - FULL FORM aligned with advertiser view */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); setDialogOpen(open); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCampaign ? "Edit Campaign" : "New Campaign"}</DialogTitle>
            <DialogDescription>
              {editingCampaign 
                ? "Update campaign settings. Changes will be reflected in the advertiser view."
                : "Create a new campaign for this advertiser."
              }
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="font-medium text-xs text-muted-foreground uppercase tracking-wider">Basic Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2 md:col-span-1">
                  <Label>Campaign Name *</Label>
                  <Input 
                    value={formData.name} 
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))} 
                    placeholder="Summer Promotion 2025" 
                    required 
                  />
                </div>
                <div className="space-y-2 col-span-2 md:col-span-1">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(v) => setFormData(prev => ({ ...prev, status: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {campaignStatuses.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea 
                  spellCheck="true"
                  value={formData.description} 
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))} 
                  placeholder="Campaign description..."
                  rows={2}
                />
              </div>
            </div>

            {/* Budget & Schedule - Label aligned with advertiser view */}
            <div className="space-y-4">
              <h3 className="font-medium text-xs text-muted-foreground uppercase tracking-wider">Budget & Schedule</h3>
              {/* Round 12, FP-30. grid-cols-3 with no breakpoint at all, so three
                  columns at every width including a phone. A native date input will not
                  compress below about 200px, so below roughly 700px the Start and End
                  inputs overflowed their cells and collided.
                  Round 2 fixed exactly this on the merchant form and the admin editor
                  kept the old layout, which is the same way FP-11's "Any" survived three
                  rounds: the fix went to the screen it was reported on. Same breakpoints
                  as the merchant form, so the two now agree. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Start Date <span className="text-red-500">*</span></Label>
                  <Input 
                    type="date" 
                    value={formData.start_at} 
                    min={!editingCampaign ? getTodayDate() : undefined}
                    onChange={(e) => setFormData(prev => ({ ...prev, start_at: e.target.value }))} 
                    required
                    className="h-9"
                    data-testid="admin-start-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">End Date <span className="text-red-500">*</span></Label>
                  <Input 
                    type="date" 
                    value={formData.end_at} 
                    min={formData.start_at || getTodayDate()}
                    onChange={(e) => setFormData(prev => ({ ...prev, end_at: e.target.value }))} 
                    required
                    className="h-9"
                    data-testid="admin-end-date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Campaign Budget ($)</Label>
                  <Input 
                    type="number" 
                    min={0}
                    step="0.01"
                    value={formData.budget_limit} 
                    onChange={(e) => setFormData(prev => ({ ...prev, budget_limit: e.target.value }))} 
                    placeholder="No limit"
                    className="h-9"
                    data-testid="admin-budget-input"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Set a maximum spend for this campaign. Once the budget is reached, the campaign will pause automatically.
              </p>
            </div>

            {/* Pricing Configuration - ALL 6 options like advertiser view */}
            <div className="space-y-4">
              <h3 className="font-medium text-xs text-muted-foreground uppercase tracking-wider">Pricing Configuration</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {/* Views */}
                <div className="flex items-center justify-between p-3 rounded-lg border">
                  <div>
                    <Label className="text-sm font-medium">Views</Label>
                    <p className="text-xs text-muted-foreground">${formData.pricing.cpm_rate}/1k views</p>
                  </div>
                  <Switch 
                    checked={formData.pricing.cpm_enabled}
                    onCheckedChange={(c) => setFormData(prev => ({ 
                      ...prev, 
                      pricing: { ...prev.pricing, cpm_enabled: c }
                    }))}
                  />
                </div>
                {/* Clicks */}
                <div className="flex items-center justify-between p-3 rounded-lg border">
                  <div>
                    <Label className="text-sm font-medium">Clicks</Label>
                    <p className="text-xs text-muted-foreground">${formData.pricing.cpc_rate}/click</p>
                  </div>
                  <Switch 
                    checked={formData.pricing.cpc_enabled}
                    onCheckedChange={(c) => setFormData(prev => ({ 
                      ...prev, 
                      pricing: { ...prev.pricing, cpc_enabled: c }
                    }))}
                  />
                </div>
                {/* Delivered Customer: Direct */}
                <div className="flex items-center justify-between p-3 rounded-lg border">
                  <div>
                    <Label className="text-sm font-medium">Delivered Customer: Direct</Label>
                    <p className="text-xs text-muted-foreground">${formData.pricing.cpdc_direct_rate}/deal</p>
                  </div>
                  <Switch 
                    checked={formData.pricing.cpdc_direct_enabled}
                    onCheckedChange={(c) => setFormData(prev => ({ 
                      ...prev, 
                      pricing: { ...prev.pricing, cpdc_direct_enabled: c }
                    }))}
                  />
                </div>
                {/* Delivered Customer: Nearby */}
                <div className="flex items-center justify-between p-3 rounded-lg border">
                  <div>
                    <Label className="text-sm font-medium">Delivered Customer: Nearby</Label>
                    <p className="text-xs text-muted-foreground">${formData.pricing.cpdc_proximity_rate}/arrival</p>
                  </div>
                  <Switch 
                    checked={formData.pricing.cpdc_proximity_enabled}
                    onCheckedChange={(c) => setFormData(prev => ({ 
                      ...prev, 
                      pricing: { ...prev.pricing, cpdc_proximity_enabled: c }
                    }))}
                  />
                </div>
                {/* The Prompted toggle was here. Prompted was absorbed into Direct on
                    2026-09-03 and is no longer a meter, so there is nothing to enable:
                    a prompted arrival bills as Direct, at the Direct rate, gated on the
                    Direct flag. Offering a switch for it would be offering to configure
                    a charge that cannot occur.

                    cpdc_prompted_enabled stays in formData because stored campaigns
                    carry it and the update model still accepts it. It gates nothing. */}
              </div>
            </div>

            {/* Targeting Configuration - ALL 8 fields like advertiser view */}
            <div className="space-y-4">
              <h3 className="font-medium text-xs text-muted-foreground uppercase tracking-wider">Targeting Configuration</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {/* Location - Opens modal like advertiser */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Location</Label>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-9 justify-between text-left font-normal"
                    onClick={() => setLocationTargetingOpen(true)}
                  >
                    <span className="truncate text-sm">
                      {getLocationTargetingSummary()}
                    </span>
                    <MapPin className="h-4 w-4 ml-2 shrink-0 opacity-50" />
                  </Button>
                </div>
                {/* Days */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Days</Label>
                  <Select 
                    value={formData.targeting_config.days.length === 7 ? "all" : formData.targeting_config.days.length === 5 ? "weekdays" : "weekends"}
                    onValueChange={(val) => setFormData(prev => ({
                      ...prev,
                      targeting_config: { 
                        ...prev.targeting_config, 
                        days: val === "all" 
                          ? ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]
                          : val === "weekdays"
                            ? ["monday", "tuesday", "wednesday", "thursday", "friday"]
                            : ["saturday", "sunday"]
                      }
                    }))}
                  >
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Days</SelectItem>
                      <SelectItem value="weekdays">Weekdays Only</SelectItem>
                      <SelectItem value="weekends">Weekends Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Time */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Time</Label>
                  <Select 
                    value={formData.targeting_config.time_slots.length === 4 ? "all" : formData.targeting_config.time_slots[0] || "all"}
                    onValueChange={(val) => setFormData(prev => ({
                      ...prev,
                      targeting_config: { 
                        ...prev.targeting_config, 
                        time_slots: val === "all" 
                          ? ["morning", "afternoon", "evening", "night"]
                          : [val]
                      }
                    }))}
                  >
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Day</SelectItem>
                      <SelectItem value="morning">Morning (6am-12pm)</SelectItem>
                      <SelectItem value="afternoon">Afternoon (12pm-6pm)</SelectItem>
                      <SelectItem value="evening">Evening (6pm-10pm)</SelectItem>
                      <SelectItem value="night">Night (10pm-6am)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Radius */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Radius</Label>
                  <Select 
                    value={formData.targeting_config.radius_override?.toString() || "default"}
                    onValueChange={(val) => setFormData(prev => ({
                      ...prev,
                      targeting_config: { ...prev.targeting_config, radius_override: val === "default" ? null : parseInt(val) }
                    }))}
                  >
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="default">Default</SelectItem>
                      <SelectItem value="250">250m</SelectItem>
                      <SelectItem value="500">500m</SelectItem>
                      <SelectItem value="1000">1km</SelectItem>
                      <SelectItem value="2000">2km</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Gender */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Gender</Label>
                  <Select 
                    value={formData.targeting_config.gender}
                    onValueChange={(val) => setFormData(prev => ({
                      ...prev,
                      targeting_config: { ...prev.targeting_config, gender: val }
                    }))}
                  >
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Age Range */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Age Range</Label>
                  <Select 
                    value={formData.targeting_config.age_ranges?.[0] || "all"}
                    onValueChange={(val) => setFormData(prev => ({
                      ...prev,
                      targeting_config: { ...prev.targeting_config, age_ranges: [val] }
                    }))}
                  >
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Ages</SelectItem>
                      <SelectItem value="18-24">18-24</SelectItem>
                      <SelectItem value="25-34">25-34</SelectItem>
                      <SelectItem value="35-44">35-44</SelectItem>
                      <SelectItem value="45-54">45-54</SelectItem>
                      <SelectItem value="55+">55+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Venue Type */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Venue Type</Label>
                  <Select 
                    value={formData.targeting_config.venue_types?.[0] || "any"}
                    onValueChange={(val) => setFormData(prev => ({
                      ...prev,
                      targeting_config: { ...prev.targeting_config, venue_types: [val] }
                    }))}
                  >
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {/* Round 12, FP-11. "Any" reads as a choice the merchant made; nothing
                          was chosen. The stored sentinel stays "any" because the
                          backend and every existing campaign use it; only the
                          label changes. Third time this has been asked for: the
                          two previous fixes were on other screens. */}
                      <SelectItem value="any">{NONE_SELECTED}</SelectItem>
                      <SelectItem value="restaurant">Restaurant</SelectItem>
                      <SelectItem value="retail">Retail</SelectItem>
                      <SelectItem value="entertainment">Entertainment</SelectItem>
                      <SelectItem value="services">Services</SelectItem>
                      <SelectItem value="nightlife">Nightlife</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Interests */}
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Interests</Label>
                  <Select 
                    value={formData.targeting_config.interests?.[0] || "any"}
                    onValueChange={(val) => setFormData(prev => ({
                      ...prev,
                      targeting_config: { ...prev.targeting_config, interests: [val] }
                    }))}
                  >
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {/* Round 12, FP-11. "Any" reads as a choice the merchant made; nothing
                          was chosen. The stored sentinel stays "any" because the
                          backend and every existing campaign use it; only the
                          label changes. Third time this has been asked for: the
                          two previous fixes were on other screens. */}
                      <SelectItem value="any">{NONE_SELECTED}</SelectItem>
                      <SelectItem value="food">Food & Dining</SelectItem>
                      <SelectItem value="shopping">Shopping</SelectItem>
                      <SelectItem value="travel">Travel</SelectItem>
                      <SelectItem value="fitness">Fitness</SelectItem>
                      <SelectItem value="technology">Technology</SelectItem>
                      <SelectItem value="entertainment">Entertainment</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Deal Settings */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium text-xs text-muted-foreground uppercase tracking-wider">Deal Settings</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">Allow passengers to save deals from in-car display</p>
                </div>
                <Switch
                  checked={formData.deal_available}
                  onCheckedChange={(checked) => setFormData(prev => ({
                    ...prev,
                    deal_available: checked
                  }))}
                />
              </div>
              
              {formData.deal_available && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Deal Description</Label>
                    <Input
                      value={formData.deal_description}
                      onChange={(e) => setFormData(prev => ({ ...prev, deal_description: e.target.value }))}
                      placeholder="e.g., 20% off your first coffee"
                      className="h-9 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Offer Text (short)</Label>
                    <Input
                      value={formData.offer_text}
                      onChange={(e) => setFormData(prev => ({ ...prev, offer_text: e.target.value }))}
                      placeholder="e.g., Save 20%"
                      className="h-9 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">CTA Button Text</Label>
                    <Input
                      value={formData.cta_text}
                      onChange={(e) => setFormData(prev => ({ ...prev, cta_text: e.target.value }))}
                      placeholder="Claim Deal"
                      className="h-9 text-sm"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Form Actions */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editingCampaign ? "Update Campaign" : "Create Campaign"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Location Targeting Modal - Same as advertiser view */}
      <Dialog open={locationTargetingOpen} onOpenChange={setLocationTargetingOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Location Targeting
            </DialogTitle>
            <DialogDescription>
              Define where ads will be shown. Target by venue locations, states, postcodes, or specific suburbs.
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="venues" className="flex-1 overflow-hidden flex flex-col">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="venues" className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Venue Locations
              </TabsTrigger>
              <TabsTrigger value="custom" className="flex items-center gap-2">
                <Map className="h-4 w-4" />
                Custom Locations
              </TabsTrigger>
            </TabsList>
            
            {/* Venue Locations Tab */}
            <TabsContent value="venues" className="flex-1 overflow-y-auto mt-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                Select which of the advertiser&apos;s registered venue locations to target.
              </p>
              
              {locations.length === 0 ? (
                <div className="text-center py-8 border rounded-lg bg-secondary/30">
                  <Building2 className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No venue locations registered</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex items-center justify-between pb-2 border-b">
                    <span className="text-sm font-medium">{locations.length} venue{locations.length !== 1 ? 's' : ''} available</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const allIds = locations.map(l => l.id);
                        const allSelected = allIds.every(id => formData.targeting_config.location_ids.includes(id));
                        setFormData(prev => ({
                          ...prev,
                          targeting_config: {
                            ...prev.targeting_config,
                            location_ids: allSelected ? [] : allIds
                          }
                        }));
                      }}
                    >
                      {locations.every(l => formData.targeting_config.location_ids.includes(l.id)) ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>
                  {locations.map(loc => (
                    <div 
                      key={loc.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        formData.targeting_config.location_ids.includes(loc.id)
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          targeting_config: {
                            ...prev.targeting_config,
                            location_ids: prev.targeting_config.location_ids.includes(loc.id)
                              ? prev.targeting_config.location_ids.filter(id => id !== loc.id)
                              : [...prev.targeting_config.location_ids, loc.id]
                          }
                        }));
                      }}
                    >
                      <Checkbox 
                        checked={formData.targeting_config.location_ids.includes(loc.id)}
                        className="pointer-events-none"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm">{loc.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{loc.address}</p>
                      </div>
                      <Badge variant="outline" className="shrink-0 text-xs">
                        {loc.radius_m || loc.radius_meters || 500}m
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
            
            {/* Custom Locations Tab */}
            <TabsContent value="custom" className="flex-1 overflow-y-auto mt-4 space-y-4">
              <p className="text-sm text-muted-foreground">
                Target broader geographic areas by state, postcode, or suburb.
              </p>
              
              {/* States Selection */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">States</Label>
                <div className="grid grid-cols-4 gap-2">
                  {AUSTRALIAN_STATES.map(state => (
                    <div
                      key={state.code}
                      className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer transition-colors text-sm ${
                        formData.targeting_config.states.includes(state.code)
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:border-primary/50'
                      }`}
                      onClick={() => {
                        setFormData(prev => ({
                          ...prev,
                          targeting_config: {
                            ...prev.targeting_config,
                            states: prev.targeting_config.states.includes(state.code)
                              ? prev.targeting_config.states.filter(s => s !== state.code)
                              : [...prev.targeting_config.states, state.code]
                          }
                        }));
                      }}
                    >
                      <Checkbox 
                        checked={formData.targeting_config.states.includes(state.code)}
                        className="pointer-events-none h-4 w-4"
                      />
                      <span>{state.code}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Suburb/Postcode Search */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Suburbs & Postcodes</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search suburbs or postcodes..."
                    value={locationSearch}
                    onChange={(e) => setLocationSearch(e.target.value)}
                    className="pl-9"
                  />
                  {searchingLocations && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>
                
                {/* Search Results */}
                {locationSearchResults.length > 0 && (
                  <div className="border rounded-lg max-h-40 overflow-y-auto">
                    {locationSearchResults.map((result, idx) => {
                      const isSuburbSelected = formData.targeting_config.suburbs.includes(result.suburb);
                      const isPostcodeSelected = formData.targeting_config.postcodes.includes(result.postcode);
                      
                      return (
                        <div
                          key={idx}
                          className={`flex items-center justify-between p-2 hover:bg-secondary/50 cursor-pointer border-b last:border-b-0 ${
                            isSuburbSelected || isPostcodeSelected ? 'bg-primary/5' : ''
                          }`}
                          onClick={() => {
                            if (result.suburb && !isSuburbSelected) {
                              setFormData(prev => ({
                                ...prev,
                                targeting_config: {
                                  ...prev.targeting_config,
                                  suburbs: [...prev.targeting_config.suburbs, result.suburb],
                                  postcodes: result.postcode && !prev.targeting_config.postcodes.includes(result.postcode)
                                    ? [...prev.targeting_config.postcodes, result.postcode]
                                    : prev.targeting_config.postcodes
                                }
                              }));
                            }
                            setLocationSearch("");
                            setLocationSearchResults([]);
                          }}
                        >
                          <div>
                            <span className="font-medium text-sm">{result.suburb}</span>
                            {result.postcode && (
                              <span className="text-muted-foreground text-sm ml-2">{result.postcode}</span>
                            )}
                            {result.state && (
                              <Badge variant="outline" className="ml-2 text-xs">{result.state}</Badge>
                            )}
                          </div>
                          <Plus className="h-4 w-4 text-muted-foreground" />
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {/* Selected Suburbs */}
                {(formData.targeting_config.suburbs.length > 0 || formData.targeting_config.postcodes.length > 0) && (
                  <div className="space-y-2 pt-2">
                    <Label className="text-xs text-muted-foreground">Selected Locations</Label>
                    <div className="flex flex-wrap gap-2">
                      {formData.targeting_config.suburbs.map(suburb => (
                        <Badge 
                          key={suburb} 
                          variant="secondary"
                          className="cursor-pointer hover:bg-destructive/20"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              targeting_config: {
                                ...prev.targeting_config,
                                suburbs: prev.targeting_config.suburbs.filter(s => s !== suburb)
                              }
                            }));
                          }}
                        >
                          {suburb}
                          <X className="h-3 w-3 ml-1" />
                        </Badge>
                      ))}
                      {formData.targeting_config.postcodes.filter(p => !formData.targeting_config.suburbs.some(s => s.includes(p))).map(postcode => (
                        <Badge 
                          key={postcode} 
                          variant="outline"
                          className="cursor-pointer hover:bg-destructive/20"
                          onClick={() => {
                            setFormData(prev => ({
                              ...prev,
                              targeting_config: {
                                ...prev.targeting_config,
                                postcodes: prev.targeting_config.postcodes.filter(p => p !== postcode)
                              }
                            }));
                          }}
                        >
                          {postcode}
                          <X className="h-3 w-3 ml-1" />
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
          
          {/* Summary */}
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium">Current Selection</span>
              <span className="text-sm text-muted-foreground">{getLocationTargetingSummary()}</span>
            </div>
            <div className="flex gap-2 justify-end">
              <Button 
                type="button"
                variant="outline" 
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    targeting_config: {
                      ...prev.targeting_config,
                      location_ids: [],
                      states: [],
                      postcodes: [],
                      suburbs: []
                    }
                  }));
                }}
              >
                Clear All
              </Button>
              <Button type="button" onClick={() => setLocationTargetingOpen(false)}>
                Done
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Modal */}
      <Dialog open={archiveModalOpen} onOpenChange={setArchiveModalOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Archive Campaign
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to archive &ldquo;{campaignToArchive?.name}&rdquo;? 
              Archived campaigns can be copied but not restored.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setArchiveModalOpen(false)} disabled={archiving}>
              Cancel
            </Button>
            <Button 
              variant="default"
              className="bg-amber-500 hover:bg-amber-600"
              onClick={confirmArchive}
              disabled={archiving}
            >
              {archiving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reprice Confirmation — a paid campaign's priced fields are locked */}
      <Dialog open={!!repriceConfirm} onOpenChange={(open) => !open && setRepriceConfirm(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Reprice a paid campaign
            </DialogTitle>
            <DialogDescription>
              &ldquo;{repriceConfirm?.campaignName}&rdquo; has already been paid for, so its
              priced fields are locked.
            </DialogDescription>
          </DialogHeader>

          {repriceConfirm?.detail && (
            <p className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm">
              {repriceConfirm.detail}
            </p>
          )}

          <p className="text-sm text-muted-foreground">
            Overriding changes what this merchant is entitled to without taking a charge or
            issuing a refund. It is recorded against your account under Admin &rsaquo; Alerts
            for review.
          </p>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setRepriceConfirm(null)} disabled={saving}>
              Cancel
            </Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600"
              onClick={confirmReprice}
              disabled={saving}
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Override and log
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
